import React, { useState, useEffect } from 'react';
import { Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Grid, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, CircularProgress, Typography } from '@mui/material';
import { PieChart, Pie, Cell, Tooltip } from 'recharts';
import { useApiService } from "../../services/ApiService/ApiService";

interface ChartData {
    name: string;
    value: number;
    percentage: string;
}

interface DonutChartProps {
    percentage: number;
    startAngle: number;
}

const COLORS = ['#bef213', '#f0f0f0']; // Highlight color and remaining grey

const DonutChart: React.FC<DonutChartProps> = ({ percentage, startAngle }) => {
    const data = [
        { name: 'Filled', value: percentage },
        { name: 'Remaining', value: 100 - percentage }
    ];

    return (
        <PieChart width={48} height={48}>
            <Pie
                data={data}
                dataKey="value"
                cx="50%"
                cy="50%"
                startAngle={90}     // Starting point at 12 o'clock position
                endAngle={-270}     // End at 12 o'clock (clockwise)
                innerRadius={2}
                outerRadius={24}
                fill="#8884d8"
                label={({ value }) => value > 0 ? `${value}%` : ''}
            >
                {data.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index]} />
                ))}
            </Pie>
            <Tooltip formatter={(value) => `${value}%`} />
        </PieChart>
    );
};

const telematicsProviders = [
    { id: '1', name: '', icon: 'https://www.teletracnavman.co.uk/media/16878/tn-logo-lockup-rgb.svg' },
    { id: '2', name: '', icon: 'https://s3.eu-west-2.amazonaws.com/fleet-smart/logo-fleetsmart.svg' },
    { id: '3', name: '', icon: 'https://www.vismo.com/images/svgs/vismo-logo.svg' },
    { id: '4', name: '', icon: 'https://go.masternaut.com/hs-fs/hubfs/Logos/FINAL%20LOGO%20MN-Michelin%2024June2019/MASTERNAUT-MICHELIN-LOGO-HOR-RGB-B_240619.png' },
    { id: '5', name: '', icon: 'https://www.verizonconnect.com/images/logo-vzc-5c1dedfa.svg' }
];

const vehicleData = [
    { make: 'Ford', model: 'eTransit', vrn: 'HK72JJK', distance: "1200km", kwh: "400kW/h" },
    { make: 'Ford', model: 'eTransit', vrn: 'HJ74EDW', distance: "900km", kwh: "300kW/h" },
    { make: 'Renault', model: 'e-Tech', vrn: 'HG24SWE', distance: "1100km", kwh: "350kW/h" },
    { make: 'Renault', model: 'e-Tech', vrn: 'HK72DSW', distance: "1200km", kwh: "400kW/h" },
    { make: 'Volvo', model: 'FH', vrn: 'ERR342', distance: "900km", kwh: "300kW/h" },
    { make: 'Volvo', model: 'FMX', vrn: 'ERR322', distance: "1100km", kwh: "350kW/h" }

];

const cpoData = [
    {
        name: 'IONITY',
        icon: 'https://www.zap-map.com/sites/default/files/media/image/2023-12/IONITY%20%C3%94%C3%87%C3%B4%20Master%20Logo_Networkguide_RGB.jpg',
        product: 'Nova',
        band: 'upto 2000kW',
        price: '£0.35/kWh',
        chartData: [70, 20, 10]

    },
    {
        name: 'Mer',
        icon: 'https://uk.mer.eco/wp-content/uploads/sites/9/2022/11/Mer_Logo_English_Positive_RGB_Endorsed-1-3200x1157.png',
        product: 'Quantum A',
        band: 'upto 1500kW',
        price: '£0.54/kWh',
        chartData: [50, 30, 20]

    },
    {
        name: 'ChargePoint',
        icon: 'https://www.chargepoint.com/themes/chargepoint/logo.svg',
        product: 'Ultra Gold',
        band: 'upto 3000kW',
        price: '£0.52/kWh',
        chartData: [50, 10, 40]

    },

    {
        name: 'GridServe',
        icon: 'https://www.gridserve.com/wp-content/uploads/2024/11/Teal-on-White-Horizontal-02.svg',
        product: 'Hyper',
        band: 'upto 4000kW',
        price: '£0.60/kWh',
        chartData: [40, 30, 30]

    },
    {
        name: 'Osprey',
        icon: 'https://electroverse.com/_next/image?url=https%3A%2F%2Fassets.electricjuice.octopus.energy%2F363x139%2F1cf85ced26%2Fosprey-charging-logo.png%2Fm%2F&w=768&q=75',
        product: 'Level 3',
        band: 'upto 3000kW',
        price: '£0.41/kWh',
        chartData: [40, 50, 10]

    }
];

const messages = [
    { text: 'Authenticating with telematics provider...', duration: 1000 },
    { text: 'Fetching vehicle data...', duration: 2000 },
    { text: 'Analyzing charging needs...', duration: 1500 },
    { text: 'Comparing Charge Point Operator products...', duration: 1000 }
];


export default function TelematicsFlow() {
    const [open, setOpen] = useState<boolean>(false);
    const [provider, setProvider] = useState<string>('');
    const [username, setUsername] = useState<string>('');
    const [password, setPassword] = useState<string>('');
    const { fetchWithAuth } = useApiService();

    interface ChartData {
        name: string;
        value: number;
        percentage: string;
    }

    interface DonutChartProps {
        percentage: number;
        startAngle: number;
    }

    const COLORS = ['#bef213', '#f0f0f0'];

    const DonutChart: React.FC<DonutChartProps> = ({ percentage, startAngle }) => {
        const data = [
            { name: 'Filled', value: percentage },
            { name: 'Remaining', value: 100 - percentage }
        ];

        return (
            <PieChart width={48} height={48}>
                <Pie
                    data={data}
                    dataKey="value"
                    cx="50%"
                    cy="50%"
                    startAngle={90}
                    endAngle={-270}
                    innerRadius={2}
                    outerRadius={24}
                    label={({ value }) => value > 0 ? `${value}%` : ''}
                >
                    {data.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index]} />
                    ))}
                </Pie>
                <Tooltip formatter={(value) => `${value}%`} />
            </PieChart>
        );
    };



    const [loading, setLoading] = useState<boolean>(false);
    const [showVehicles, setShowVehicles] = useState<boolean>(false);
    const [currentMessage, setCurrentMessage] = useState(messages[0].text);


    const handleConnect2 = () => {
        setLoading(true);
        let timeout: NodeJS.Timeout;
        let index = 0;

        const showNextMessage = () => {
            if (index < messages.length - 1) {
                timeout = setTimeout(() => {
                    index++;
                    setCurrentMessage(messages[index].text);
                    showNextMessage();
                }, messages[index].duration);
            } else {
                setTimeout(() => {
                    setLoading(false);
                    setShowVehicles(true);
                }, messages[index].duration);
            }
        };

        showNextMessage();
    };

    const handleConnect = () => {
        setOpen(false);
        setShowVehicles(false);
        handleConnect2();
    };

    const handleStart = () => {
        setOpen(true);
        setShowVehicles(false);
        setUsername('');
        setPassword('');
    };

    return (
        <div style={{ padding: '20px' }}>
            <Button variant="contained" color="primary" onClick={handleStart}>Start</Button>
            <br />
            <br />
            <Dialog open={open} onClose={() => setOpen(false)}>
                <DialogTitle>Select Telematics Provider</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2}>
                        {telematicsProviders.map((item) => (
                            <Grid item xs={6} key={item.id}>
                                <div
                                    onClick={() => setProvider(item.id)}
                                    style={{
                                        border: provider === item.id ? '2px solid blue' : '1px solid gray',
                                        borderRadius: '8px',
                                        padding: '10px',
                                        textAlign: 'center',
                                        cursor: 'pointer'
                                    }}
                                >
                                    <img src={item.icon} alt={item.name} style={{ width: '100%', maxHeight: '60px', objectFit: 'contain' }} />
                                    <Typography>{item.name}</Typography>
                                </div>
                            </Grid>
                        ))}
                    </Grid>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Username"
                        type="text"
                        fullWidth
                        variant="outlined"
                        value={username}
                        onChange={(e) => setUsername(e.target.value)}
                    />
                    <TextField
                        margin="dense"
                        label="Password"
                        type="password"
                        fullWidth
                        variant="outlined"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpen(false)}>Cancel</Button>
                    <Button onClick={handleConnect} color="primary">Connect</Button>
                </DialogActions>
            </Dialog>

            <div style={{ padding: '20px' }}>
                {loading && (
                    <div style={{ textAlign: 'center', marginTop: '20px' }}>
                        <CircularProgress />
                        <Typography variant="h6">{currentMessage}</Typography>
                    </div>
                )}
            </div>

            {showVehicles && (
                <div>
                    <Typography variant="h6" style={{ marginTop: '20px' }}>Vehicles</Typography>
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Make</TableCell>
                                    <TableCell>Model</TableCell>
                                    <TableCell>VRN</TableCell>
                                    <TableCell>Average Monthly Distance</TableCell>
                                    <TableCell>Estimated Monthly Charging Requirement</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {vehicleData.map((vehicle, index) => (
                                    <TableRow key={index}>
                                        <TableCell>{vehicle.make}</TableCell>
                                        <TableCell>{vehicle.model}</TableCell>
                                        <TableCell>{vehicle.vrn}</TableCell>
                                        <TableCell>{vehicle.distance}</TableCell>
                                        <TableCell>{vehicle.kwh}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <br />
                    <Typography variant="h6" style={{ marginTop: '20px' }}>Available Products</Typography>
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Icon</TableCell>
                                    <TableCell>Product Name</TableCell>
                                    <TableCell>kW/h Band</TableCell>
                                    <TableCell>Price per kW/h</TableCell>
                                    <TableCell>% within 25 km*</TableCell>
                                    <TableCell>% within 50km*</TableCell>
                                    <TableCell>% over 50km*</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {cpoData.map((cpo, index) => (
                                    <TableRow key={index}>
                                        <TableCell><img src={cpo.icon} alt={cpo.name} style={{ height: '40px' }} /></TableCell>
                                        <TableCell>{cpo.product}</TableCell>
                                        <TableCell>{cpo.band}</TableCell>
                                        <TableCell>{cpo.price}</TableCell>
                                        <TableCell><DonutChart percentage={cpo.chartData[0]} startAngle={0} /></TableCell>
                                        <TableCell><DonutChart percentage={cpo.chartData[1]} startAngle={cpo.chartData[0] * 3.6} /></TableCell>
                                        <TableCell><DonutChart percentage={cpo.chartData[2]} startAngle={(cpo.chartData[0] + cpo.chartData[1]) * 3.6} /></TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <Typography style={{ marginTop: '20px', textAlign: 'right' }}>* % of charge points within identified required charging range</Typography>

                </div>
            )}
        </div>
    );
}
